import supabase from '../supabase/supabase';

const TABLE_NAME = 'qrcodes';

/**
 * Generates a random 6-character alphanumeric slug
 */
const generateSlug = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let slug = '';
  for (let i = 0; i < 6; i++) {
    slug += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return slug;
};

/**
 * Fetches user profile including their QR code quota
 */
export const fetchUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Fetches all QR codes for a specific user
 */
export const fetchUserQRCodes = async (userId) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

/**
 * Creates a new dynamic QR code with quota check
 */
export const createQRCode = async (destinationUrl, userId) => {
  try {
    // 1. Check Quota
    const { data: profile } = await supabase
      .from('profiles')
      .select('max_codes')
      .eq('id', userId)
      .single();

    const { count } = await supabase
      .from(TABLE_NAME)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (count >= profile.max_codes) {
      throw new Error(`Quota reached. You can only create ${profile.max_codes} QR code(s).`);
    }

    // 2. Generate Unique Slug
    let slug = generateSlug();
    let isUnique = false;
    let attempts = 0;
    
    while (!isUnique && attempts < 5) {
      const { data: existing } = await supabase
        .from(TABLE_NAME)
        .select('slug')
        .eq('slug', slug)
        .maybeSingle();
      
      if (!existing) {
        isUnique = true;
      } else {
        slug = generateSlug();
        attempts++;
      }
    }

    // 3. Insert
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert([
        { user_id: userId, slug, destination_url: destinationUrl }
      ])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creating QR code:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Updates the destination URL for an existing QR code
 */
export const updateQRCodeDestination = async (id, newUrl) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .update({ destination_url: newUrl })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Increments the scan count for a specific QR code using RPC
 */
export const incrementScanCount = async (id) => {
  const { error } = await supabase.rpc('increment_scan', { row_id: id });
  if (error) console.error('[Tracking Error]:', error);
};